import React, { useCallback, useState, useRef } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  // TextField,
  Typography,
  Grid,
  CircularProgress
} from "@material-ui/core";
import useStyles from "@/styles/dialogStyle";
import { useTranslation } from "@/langs/useTranslation";
import styles from "./index.module.less";
import { IconAndroid, IconClose } from "@/components/Icon";
import { s3upload } from "@/helpers/s3upload";
import { androidAppUploadCheck } from "@/apis/apiCollection";
import { toast } from "@/apis/request";
// import UploadImage from "@/components/UploadImage";

const UploadAndroidAppDialog = ({
  open,
  onClose,
  updateAppInfo,
  setRefreshCapacity,
  reloadTable,
  onSuccess
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [appInfo, setAppInfo] = useState({
    iconFile: "",
    name: "",
    fileName: "",
    package: "",
    version: "",
    file: null
  });
  const [analysing, setAnalysing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showNameError, setShowNameError] = useState(false);
  const [percentCompleted, setPercentCompleted] = useState(0);
  const uploadRef = useRef(null);
  const [appImageSrc, setAppImageSrc] = useState(null);
  /**
   * uint8Array转Blob
   * @param uint8Array
   * @param mimeType
   * @returns {module:buffer.Blob}
   */
  const uint8ArrayToBlob = (uint8Array, mimeType) => {
    return new Blob([uint8Array], { type: mimeType });
  };
  const blobToBase64 = blob => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const displayImage = async blob => {
    const base64Image = await blobToBase64(blob);
    setAppImageSrc(base64Image);
  };

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length === 0) {
      toast.error(t({ defaultMessage: "不支持的格式。" }));
      return;
    }
    const fileName = acceptedFiles[0].name;
    setAnalysing(true);

    try {
      import("web-pkg-parser").then(({ Application }) => {
        Application.loadAsync(acceptedFiles[0]).then(
          application => {
            const blob = uint8ArrayToBlob(application.iconSteam, "image/png");
            displayImage(blob);
            setAppInfo({
              name: application.name,
              package: application.package,
              version: application.versionName,
              versionCode: application.versionCode,
              fileName,
              file: acceptedFiles[0],
              iconFile: blob
            });
            setAnalysing(false);
            // package versionName
          },
          () => {
            toast.error(
              t({ defaultMessage: "上传失败，该应用程序无法解析。" })
            );
            setAnalysing(false);
          }
        );
      });
    } catch (e) {
      toast.error(t({ defaultMessage: "上传失败，该应用程序无法解析。" }));
      setAnalysing(false);
    }
    // todo 使用worker来进行这个过程
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noDrag: uploading,
    noClick: uploading,
    accept: {
      "application/vnd.android.package-archive": [".apk"],
      "application/vnd.android.package": [".apk"]
    }
  });

  const wrongPackage =
    updateAppInfo &&
    appInfo.package &&
    updateAppInfo.package !== appInfo.package;

  const checkCanUpload = async () => {
    if (!appInfo.name) {
      setShowNameError(true);
      return false;
    }
    if (wrongPackage) return false;

    try {
      const res = await androidAppUploadCheck({
        package_id: appInfo.package,
        version_code: appInfo.versionCode
      });
      const versionCodeCheck = res.message === "success";
      if (!versionCodeCheck) return false;
    } catch (e) {
      if (e.response?.data?.errors?.commit_failed) {
        setTimeout(() => {
          toast.closeAll();
          toast.error(t({ defaultMessage: "版本代码已存在，请重新上传" }));
        });
      }
      return false;
    }
    return true;
  };

  const handleDeleteFile = useCallback(e => {
    e.stopPropagation();
    setAppInfo({});
  }, []);

  const onCloseDialog = useCallback(
    forceClose => {
      if (!forceClose && uploading) return;
      setAppInfo({});
      setUploading(false);
      setAnalysing(false);
      setPercentCompleted(0);
      setAppImageSrc(null);
      onClose();
    },
    [uploading]
  );

  const onOk = async () => {
    const canUpload = await checkCanUpload();
    if (!canUpload) return;
    setUploading(true);
    uploadRef.current = await s3upload(
      { files: [appInfo.file] },
      "",
      t,
      t({ defaultMessage: "上传Android应用程序成功" }),
      percentCompleted,
      setPercentCompleted,
      () => onCloseDialog(true),
      setRefreshCapacity,
      reloadTable,
      true,
      false,
      false,
      true,
      false,
      false,
      undefined,
      "android",
      {
        package_name: appInfo.name,
        package_id: appInfo.package,
        version: appInfo.version,
        version_code: appInfo.versionCode,
        iconFile: appInfo.iconFile
      },
      onSuccess
    );
  };

  const handleChangeIcon = imageInfo => {
    setAppInfo({
      ...appInfo,
      iconFile: imageInfo.file
    });
  };

  return (
    <Dialog
      maxWidth={false}
      className={classes.dialog}
      open={open}
      onClose={onCloseDialog}
      aria-labelledby="add-file"
      aria-describedby="add-file-confirmation"
      disableBackdropClick={uploading || analysing}
      disableEscapeKeyDown={uploading || analysing}
    >
      <DialogTitle id="add-file" style={{ paddingTop: "12px !important" }}>
        {updateAppInfo
          ? t({ defaultMessage: "更新Android应用程序" })
          : t({ defaultMessage: "上传Android应用程序" })}
      </DialogTitle>
      <DialogContent className="uploadFileOnlyDialog" style={{ width: 560 }}>
        {updateAppInfo ? (
          <div>
            <Typography variant="subtitle1">
              {t({ defaultMessage: "当前程序信息" })}
            </Typography>
            <div className={styles.currentAppInfo}>
              <Box
                component="label"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Grid item xs={2}>
                  <Typography>{t({ defaultMessage: "程序名称：" })}</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant="subtitle1">
                    {updateAppInfo.name}
                  </Typography>
                </Grid>
              </Box>
              <Box
                component="label"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Grid item xs={2}>
                  <Typography>{t({ defaultMessage: "版本号：" })}</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant="subtitle1">
                    {updateAppInfo.version}
                  </Typography>
                </Grid>
              </Box>
              <Box
                component="label"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Grid item xs={2}>
                  <Typography>{t({ defaultMessage: "程序包ID：" })}</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant="subtitle1">
                    {updateAppInfo.package}
                  </Typography>
                </Grid>
              </Box>
              <Box
                component="label"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Grid item xs={2}>
                  <Typography>{t({ defaultMessage: "版本代码：" })}</Typography>
                </Grid>
                <Grid item xs={10}>
                  <Typography variant="subtitle1">
                    {updateAppInfo.versionCode}
                  </Typography>
                </Grid>
              </Box>
            </div>
          </div>
        ) : (
          <>
            {appImageSrc && (
              <Box
                component="label"
                sx={{ display: "flex", alignItems: "center", marginTop: 24 }}
              >
                <Typography variant="subtitle1">
                  {t({ defaultMessage: "App icon：" })}
                </Typography>
              </Box>
            )}
            {appImageSrc && (
              <img
                src={appImageSrc}
                alt="app icon"
                style={{ width: 76, height: 76, marginTop: 10 }}
              />
            )}
            {/*<UploadImage onChange={handleChangeIcon}/>*/}

            {/*<Box component="label" sx={{display: 'flex', alignItems: 'center'}}>*/}
            {/*  <Typography variant="subtitle1">*/}
            {/*    {t({defaultMessage: '程序名称：'})}*/}
            {/*  </Typography>*/}
            {/*  <Typography variant="body1" color="error">*/}
            {/*    **/}
            {/*  </Typography>*/}
            {/*</Box>*/}
            {/*<TextField*/}
            {/*  fullWidth*/}
            {/*  value={appInfo.name}*/}
            {/*  onChange={e => {*/}
            {/*    return*/}
            {/*    setAppInfo(v => {*/}
            {/*      const newValue = {...v}*/}
            {/*      newValue.name = e.target?.value*/}
            {/*      return newValue*/}
            {/*    })*/}
            {/*  }}*/}
            {/*  disabled={uploading}*/}
            {/*  variant="outlined"*/}
            {/*  size="small"*/}
            {/*  style={{marginTop: 8}}*/}
            {/*/>*/}
            {showNameError && (
              <Typography color="error">
                {t({ defaultMessage: "程序名称必填" })}
              </Typography>
            )}
          </>
        )}
        <Typography variant="subtitle1" style={{ marginTop: 24 }}>
          {t({ defaultMessage: "上传Android应用程序" })}
        </Typography>
        <div
          className={`${styles.uploadArea} ${
            isDragActive ? styles.dragActive : ""
          } ${uploading ? styles.uploading : ""}`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          {uploading || analysing ? (
            <Box display="flex" alignItems="center">
              <CircularProgress size={14} />
              <Typography style={{ marginLeft: 10, color: "#91919F" }}>
                {uploading
                  ? t(
                      { defaultMessage: "正在上传 {percentCompleted}%" },
                      { percentCompleted }
                    )
                  : t({ defaultMessage: "解析中" })}
              </Typography>
            </Box>
          ) : (
            <>
              {appInfo.package ? (
                <div className={styles.uploadInfo}>
                  {/*<Typography className={styles.uploadTip}>*/}
                  {/*  {appInfo.fileName}*/}
                  {/*</Typography>*/}
                  <Typography className={styles.uploadTip1}>
                    {t(
                      { defaultMessage: "程序名称：{name}" },
                      { name: appInfo.name }
                    )}
                    <IconAndroid size={24} color="#00AFC6" />
                  </Typography>
                  <Typography className={styles.uploadTip2}>
                    {t(
                      { defaultMessage: "版本号：{version}" },
                      { version: appInfo.version }
                    )}
                  </Typography>
                  <Typography className={styles.uploadTip2}>
                    {t(
                      { defaultMessage: "版本代码：{versionCode}" },
                      { versionCode: appInfo.versionCode }
                    )}
                  </Typography>
                  <Typography
                    className={styles.uploadTip2}
                    color={wrongPackage ? "error" : undefined}
                  >
                    {t(
                      { defaultMessage: "程序包ID：{package}" },
                      { package: appInfo.package }
                    )}
                  </Typography>
                  <div className={styles.closeBtn} onClick={handleDeleteFile}>
                    <IconClose size={10} color="rgb(245,74,68)" />
                  </div>
                </div>
              ) : (
                <>
                  <IconAndroid size={58} color="#00AFC6" />
                  <Typography className={styles.uploadTip}>
                    {t({ defaultMessage: "上传Android应用程序" })}
                  </Typography>
                  <Typography className={styles.uploadTip2}>
                    {t({ defaultMessage: "将文件拖到此处 或点击上传" })}
                  </Typography>
                </>
              )}
            </>
          )}
        </div>
        {wrongPackage && (
          <Typography color="error">
            {t({ defaultMessage: "程序包ID不一致" })}
          </Typography>
        )}
        <Box display="flex" justifyContent="center" sx={{ marginTop: 32 }}>
          <Button
            variant="contained"
            color="primary"
            style={{ width: 150, height: 40 }}
            onClick={onOk}
            disabled={!appInfo.file || uploading}
          >
            {t({ defaultMessage: "确认" })}
          </Button>
          {uploading ? (
            <Button
              color="secondary"
              variant="outlined"
              type="button"
              style={{ width: 150, height: 40, marginLeft: 10 }}
              onClick={() => {
                if (uploadRef.current) {
                  uploadRef.current.abort();
                }
                onCloseDialog(true);
              }}
              // disabled={uploading}
            >
              {t({ defaultMessage: "取消上传" })}
            </Button>
          ) : (
            <Button
              variant="contained"
              // color="secondary"
              style={{
                width: 150,
                height: 40,
                background: "rgba(0, 175, 198, 0.1)",
                color: uploading ? undefined : "#00afc6",
                marginLeft: 10
              }}
              onClick={onCloseDialog}
            >
              {t({ defaultMessage: "取消" })}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UploadAndroidAppDialog;
